import * as React from 'react'
import dynamic from 'next/dynamic'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as models from '@owl-nest/models'
import { t, tc } from '@owl-nest/localize'

import { type FormValues } from '@owl-nest/tip'
const Tip = dynamic(() => import('@owl-nest/tip').then((mod) => mod.Tip), { ssr: false })

type TipBlockProps = {
  project: models.project.ProjectForOwner
}

export function TipBlock({ project }: TipBlockProps): React.ReactElement {
  const isMember = models.project.isUserMember(project)

  return (
    <TipBlockLayout isMember={isMember}>
      {isMember ? (
        <TextSection>
          <plume.styles.heading.XXS asDesktop="M">
            {tc('Support with [highlight: a donation!]', {
              highlight: <plume.HighlightedTitle />,
            })}
          </plume.styles.heading.XXS>
          <plume.styles.copy.S>{t('A little extra help')}</plume.styles.copy.S>
        </TextSection>
      ) : (
        <TextSection>
          <plume.styles.heading.XXS asDesktop="M">
            {tc('or just [highlight: make a donation!]', {
              highlight: <plume.HighlightedTitle />,
            })}
          </plume.styles.heading.XXS>
          <plume.styles.copy.S>{t('By donating, you become a free member.')}</plume.styles.copy.S>
        </TextSection>
      )}
      <TipSection>
        <Tip
          withPresets={true}
          presetsTip={[5, 10, 20]}
          project={project}
          disableCta={false}
          allowNullValue={true}
          handleFormSubmit={(values: FormValues) =>
            (window.location.href = `/projects/${project.id}/checkout/?filter=donation&tip=${values.donation}`)
          }
        />
      </TipSection>
    </TipBlockLayout>
  )
}

const TipBlockLayout = styled.div<{ isMember?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 900px;
  position: relative;
  margin-bottom: 60px;

  &::before {
    content: '';
    position: absolute;
    top: 224px;
    left: 50%;
    transform: translateX(-50%);
    height: 169px;
    width: 100%;
    background: ${({ theme }) => `${theme?.colors?.background ?? plume.COLORS.PRIMARY_BLUE_100}`};
    z-index: -1;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      border-radius: 4px;
      top: unset;
      height: 155px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-direction: row;
    justify-content: space-evenly;
    height: 155px;
    margin-inline: auto;
    margin-top: 100px;
    margin-bottom: ${(props) => (props.isMember ? '0px' : '80px')};
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    justify-content: space-between;
    padding-inline: 60px;
  }
`

const TipSection = styled.div`
  display: flex;
  max-width: 428px;
  width: 100%;
  min-height: 100%;
  height: auto;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
  margin-inline: 16px;
  border-radius: 4px;
  background: ${plume.COLORS.PRIMARY_GREY_000};
  box-shadow: ${plume.EFFECTS.SHADOW_3};

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    top: -42px;
    margin-inline: 0;
  }
`

const TextSection = styled.div`
  position: relative;
  text-align: center;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  width: fit-content;
  margin: 40px 20px 24px;

  ${plume.styles.heading.Base} {
    width: 100%;
  }

  @media screen and ${plume.BREAKPOINTS.MOBILE_L} {
    text-align: left;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    top: unset;
    text-align: left;
    margin: 0;
  }
`
